/**
 * Copy bilingüe (ES/EN) de las cuatro páginas dedicadas que antes eran
 * únicamente secciones ancla de la home: /como-funciona, /estrategia,
 * /agente-ia y /tarifas.
 *
 * Se mantiene centralizado aquí (mismo criterio que `businessCases.ts`)
 * para que ningún componente tenga literales en un solo idioma.
 */

export type LandingPageKey = "howItWorks" | "strategy" | "aiAgent" | "pricing";

export interface LandingPageCopy {
  /** Ruta canónica en español */
  pathEs: string;
  /** Ruta canónica en inglés */
  pathEn: string;
  /** Ancla equivalente dentro de la home */
  anchor: string;
  title: { es: string; en: string };
  description: { es: string; en: string };
  h1: { es: string; en: string };
  intro: { es: string; en: string };
  keywords: { es: string; en: string };
}

export const LANDING_PAGES: Record<LandingPageKey, LandingPageCopy> = {
  howItWorks: {
    pathEs: "/como-funciona",
    pathEn: "/en/how-it-works",
    anchor: "#sectores",
    title: {
      es: "Cómo funciona Retail Lemon: aprende a llevar tu negocio paso a paso",
      en: "How Retail Lemon works: learn to run your shop step by step",
    },
    description: {
      es: "Cuentas tu problema a Sara IA, sigues sus pasos, usas las herramientas y, si se complica, entra un profesional. Prueba Club Pro 7 días gratis.",
      en: "Tell Sara AI your problem, follow her steps, use the tools and, if it gets tricky, a professional steps in. Try Club Pro free for 7 days.",
    },
    h1: {
      es: "Cómo funciona Retail Lemon",
      en: "How Retail Lemon works",
    },
    intro: {
      es: "Nada de teoría genérica: el método se adapta a la realidad diaria de tu sector, con hojas de ruta accionables y checklists que puedes ejecutar desde hoy.",
      en: "No generic theory: the method adapts to the daily reality of your sector, with actionable roadmaps and checklists you can start executing today.",
    },
    keywords: {
      es: "método retail, cómo funciona retail lemon, hoja de ruta retail, checklist tienda",
      en: "retail method, how retail lemon works, retail roadmap, store checklist",
    },
  },
  strategy: {
    pathEs: "/estrategia",
    pathEn: "/en/strategy",
    anchor: "#estrategia",
    title: {
      es: "La Estrategia Retail Lemon: tu panel de control paso a paso",
      en: "The Retail Lemon Strategy: your step-by-step control panel",
    },
    description: {
      es: "La estrategia de Retail Lemon en un panel único: módulos, progreso real, métricas y las decisiones que mueven el margen de tu negocio de retail.",
      en: "The Retail Lemon strategy in a single panel: modules, real progress, metrics and the decisions that move your retail margin.",
    },
    h1: {
      es: "La Estrategia: un panel, todas tus decisiones",
      en: "The Strategy: one panel, every decision",
    },
    intro: {
      es: "Tu negocio ordenado por módulos, con progreso medible y prioridades claras. Sin hojas de cálculo dispersas ni intuiciones sueltas.",
      en: "Your business organised by modules, with measurable progress and clear priorities. No scattered spreadsheets, no loose hunches.",
    },
    keywords: {
      es: "estrategia retail, panel de control retail, gestión de tienda, kpis retail",
      en: "retail strategy, retail control panel, store management, retail kpis",
    },
  },
  aiAgent: {
    pathEs: "/agente-ia",
    pathEn: "/en/ai-agent",
    anchor: "#agente-ia",
    title: {
      es: "Agente Retail IA para Retail + Apoyo Humano | Retail Lemon",
      en: "Retail AI Agent for retail + expert human support | Retail Lemon",
    },

    description: {
      es: "Un Agente Retail IA que te enseña los pasos de tu negocio de retail, con la opción de apoyo humano experto cuando la decisión es grande.",
      en: "The Retail AI Agent that walks you through your retail business, with optional expert human support when the decision is a big one.",
    },
    h1: {
      es: "Un Agente Retail IA que te enseña los pasos",
      en: "The Retail AI Agent that teaches you the steps",
    },
    intro: {
      es: "La IA te guía cada día dentro del panel; las personas entran cuando hace falta criterio y responsabilidad. Ni chatbot vacío ni consultoría inaccesible.",
      en: "AI guides you daily inside the panel; humans step in when judgement and accountability are needed. Neither an empty chatbot nor unaffordable consulting.",
    },
    keywords: {
      es: "agente retail ia, inteligencia artificial tiendas, mentor ia negocio, apoyo humano consultoría",
      en: "Retail AI Agent, artificial intelligence for stores, ai business mentor, human expert support",
    },
  },
  pricing: {
    pathEs: "/tarifas",
    pathEn: "/en/pricing",
    anchor: "#tarifas",
    // El título abre con los planes del panel; los extras se mencionan en la
    // descripción, nunca al inicio del title.
    title: {
      es: "Tarifas: Básico, Club Pro y Avanzado | Retail Lemon",
      en: "Pricing: Basic, Club Pro and Advanced | Retail Lemon",
    },
    description: {
      es: "Tarifas de Retail Lemon: Básico 39 €/mes, Club Pro 69 €/mes y Avanzado 99 €/mes. Todos los planes son mensuales y sin permanencia, e incluyen 7 días de prueba gratis.",
      en: "Retail Lemon pricing: Basic €39/month, Club Pro €69/month and Advanced €99/month. All plans are monthly with no minimum term and include a 7-day free trial.",
    },
    h1: {
      es: "Tarifas de Retail Lemon",
      en: "Retail Lemon pricing",
    },
    intro: {
      es: "Tres planes, un solo método. Empieza por el que necesites y sube cuando tu negocio lo pida: todos los planes son mensuales y sin permanencia.",
      en: "Three plans, one method. Start with the one you need and upgrade when your business asks for it: all plans are monthly with no minimum term.",
    },
    keywords: {
      es: "tarifas retail lemon, precios club pro, plan básico retail, plan avanzado retail, consultoría retail tarifas",
      en: "retail lemon pricing, club pro price, basic retail plan, advanced retail plan, retail consulting fees",
    },
  },
};
